import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CircleCheck, Smartphone, Crown, Lock } from 'lucide-react';
import { isValidCategory, getCategoryDisplayName } from '../config/categoryConfig';
import { StockCategoryFilter } from '../types/stockCategories';
import { useCompletedDays } from '../hooks/useCompletedDays';
import { useEntitlement } from '../hooks/useEntitlement';
import { UpgradePromptModal } from '../components/ui/modals/UpgradePromptModal';
import { cn } from '../utils/cn';

/**
 * Archive page showing the last 30 days for a category.
 * Premium users can click to replay; non-premium users see an upgrade prompt.
 */
export const CategoryArchivePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { is_premium } = useEntitlement();
  const [showUpgrade, setShowUpgrade] = useState(false);

  // Extract category from pathname (e.g. "/popular/archive" → "popular")
  const category = location.pathname.split('/')[1];
  const validCategory = category && isValidCategory(category) ? (category as StockCategoryFilter) : null;

  const days = useMemo(() => {
    const result: { date: string; dayLabel: string; dateLabel: string; isToday: boolean }[] = [];
    const now = new Date();

    for (let i = 0; i < 30; i++) {
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - i);
      const iso = d.toISOString().split('T')[0];
      const dayLabel = i === 0
        ? 'Today'
        : d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
      const dateLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      result.push({ date: iso, dayLabel, dateLabel, isToday: i === 0 });
    }
    return result;
  }, []);

  const dateStrings = useMemo(() => days.map((d) => d.date), [days]);
  const { completedDates } = useCompletedDays(validCategory || ('popular' as StockCategoryFilter), dateStrings);

  if (!validCategory) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground">Invalid category.</p>
      </div>
    );
  }

  const displayName = getCategoryDisplayName(validCategory);

  const handleDayClick = (date: string, isToday: boolean) => {
    if (isToday) {
      navigate(`/${validCategory}`);
    } else if (is_premium) {
      navigate(`/${validCategory}?date=${date}`);
    } else {
      setShowUpgrade(true);
    }
  };

  return (
    <>
      <div className="mx-auto max-w-lg px-4 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/${validCategory}`)}
            className="p-1.5 rounded-lg hover:bg-black/5 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-foreground">{displayName} Archive</h1>
            <p className="text-xs text-muted-foreground">Past 30 days</p>
          </div>
        </div>

        {/* Premium banner for free users */}
        {!is_premium && (
          <div className="flex items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <Crown className="w-5 h-5 text-amber-500 shrink-0" />
            <p className="text-xs text-amber-800 leading-snug">
              Upgrade to a Wallstreetle Membership to unlock 250+ archived games across all 9 categories.
            </p>
          </div>
        )}

        {/* Day grid */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {days.map(({ date, dayLabel, dateLabel, isToday }) => {
            const isCompleted = completedDates.has(date);
            return (
              <button
                key={date}
                onClick={() => handleDayClick(date, isToday)}
                className={cn(
                  'relative flex flex-col items-center gap-1 rounded-xl border border-black/[0.06] px-2 pt-3.5 pb-3 text-center transition-all duration-[250ms] ease-in-out',
                  isToday
                    ? 'bg-[#1a1a1a] border-black/[0.15]'
                    : 'bg-[#f0eeea] hover:bg-[#e8e6e1]',
                  !isToday && !is_premium && 'opacity-70'
                )}
              >
                {/* Crown or Lock badge for past days */}
                {!isToday && (
                  is_premium ? (
                    <Crown className="absolute top-1 right-1 h-[11px] w-[11px] text-amber-600 opacity-60" />
                  ) : (
                    <Lock className="absolute top-1 right-1 h-[11px] w-[11px] text-black/25" />
                  )
                )}

                {/* Status icon */}
                {isCompleted ? (
                  <CircleCheck className={cn('h-4 w-4', isToday ? 'text-white/60' : 'text-green-500')} />
                ) : (
                  <Smartphone className={cn('h-4 w-4', isToday ? 'text-white/60' : 'text-black/20')} />
                )}

                <span className={cn('text-[13px] font-bold', isToday ? 'text-white' : 'text-black/75')}>
                  {dayLabel}
                </span>
                <span className={cn('text-[11px] font-medium leading-tight', isToday ? 'text-white/60' : 'text-black/35')}>
                  {dateLabel}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <UpgradePromptModal
        isOpen={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Unlock Past Games"
        message="Upgrade to a Wallstreetle Membership to unlock 250+ archived games across all 9 categories."
        requiresPremium={true}
        feature="Past Games"
      />
    </>
  );
};

export default CategoryArchivePage;
